import { Color } from "@repo/app-types/constants/colors";
import type { Contact } from "@repo/app-types/entities/contact";
import type { Interview } from "@repo/app-types/entities/interview";
import type { KeywordTracker } from "@repo/app-types/entities/keyword";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { IconMinus, IconPlus } from "@tabler/icons-react";
import { type FC, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { twMerge } from "tailwind-merge";
import { useAppSelector } from "../../../hooks/hook.js";
import { selectTranscriptFragmentsByKeyword } from "../../transcriptFragments/transcriptFragmentsState.js";
import { ContactNameWithTranscriptFragment } from "../contactNameWithTranscriptFragment.js";
import { InsightBar } from "../insightBar.js";
import { selectKeywordTrackerMatchesForTrackerId } from "../insightsSlice.js";
import { KeywordAnalysisCard } from "./keywordAnalysisCard.js";

export interface KeywordTrackerCardProps {
  keywordTracker: KeywordTracker;
  contactsDictionary: Record<string, Contact>;
  interviewsDictionary: Record<string, Interview>;
  interviewCount: number;
}

// Currently this card is only used for competitive keyword trackers
export const KeywordTrackerCard: FC<KeywordTrackerCardProps> = ({
  keywordTracker,
  contactsDictionary,
  interviewsDictionary,
  interviewCount,
}) => {
  const navigate = useNavigate();
  const [isQuoteSectionExpanded, setIsQuoteSectionExpanded] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const { keywords } = keywordTracker;
  const keywordTrackerMatches = useAppSelector(
    selectKeywordTrackerMatchesForTrackerId(keywordTracker.id),
  );

  const sortedKeywords = useMemo(() => {
    return keywords.toSorted((a, b) => {
      // Sort keywords from most to least matches
      const aMatchCount =
        keywordTrackerMatches?.[a.phrase]?.interview_ids.length || 0;
      const bMatchCount =
        keywordTrackerMatches?.[b.phrase]?.interview_ids.length || 0;
      return bMatchCount - aMatchCount;
    });
  }, [keywords, keywordTrackerMatches]);

  const selectedKeyword = sortedKeywords[selectedIndex];

  const transcriptFragments = useAppSelector(
    selectTranscriptFragmentsByKeyword(keywordTracker.id, selectedKeyword),
  );

  const renderQuotesSection = () => {
    if (selectedKeyword === undefined) return null;

    if (transcriptFragments.length === 0)
      return (
        <div>{`"${selectedKeyword.phrase}" hasn't appeared in any interviews yet.`}</div>
      );

    // If we don't have any analysis, we just show the quotes without any ability to show/hide
    if (!isQuoteSectionExpanded && selectedKeyword.analyses.length > 0) {
      return (
        <div>
          <Button
            variant={ButtonVariant.Secondary}
            icon={<IconPlus className="h-4 w-4" />}
            onClick={() => setIsQuoteSectionExpanded(true)}
          >
            Show interview quotes
          </Button>
        </div>
      );
    }

    return (
      <>
        {transcriptFragments.map((tf) => {
          const interview = interviewsDictionary[tf.interview_id];
          if (!interview) return null; // Shouldn't happen with proper state management

          const contact = contactsDictionary[interview.contact_id];
          if (!contact) return null; // Shouldn't happen with proper state management

          if (!tf.text_transcript) return null;

          return (
            <ContactNameWithTranscriptFragment
              key={`keyword-tf-${tf.id}`}
              transcriptFragments={[tf]}
              date={new Date(tf?.start_time)}
              contact={contact}
              onClick={() => {
                // Add interview_id query parameter to the current location
                navigate({
                  search: `?interview_id=${interview.id}&f=${tf.id}`,
                });
              }}
              shouldHideQuestion
            />
          );
        })}
        {selectedKeyword.analyses.length > 0 ? (
          <div>
            <Button
              variant={ButtonVariant.Secondary}
              icon={<IconMinus className="h-4 w-4" />}
              onClick={() => setIsQuoteSectionExpanded(false)}
            >
              Hide interview quotes
            </Button>
          </div>
        ) : null}
      </>
    );
  };

  if (sortedKeywords.length === 0 || selectedKeyword === undefined) {
    return (
      <div className="flex flex-row divide-y divide-gray-100 bg-white text-sm leading-6">
        <div className="p-4">
          As more interviews are completed, AI-generated themes will be shown
          here.
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-row divide-y divide-gray-100 bg-white text-sm leading-6">
      <div className="max-h-[1000px] w-[200px] shrink-0 overflow-y-auto">
        {sortedKeywords.map((entry, index) => {
          const interviewPercentage =
            interviewCount > 0
              ? Math.round(
                  ((keywordTrackerMatches?.[entry.phrase]?.interview_ids
                    .length || 0) /
                    interviewCount) *
                    100,
                )
              : 0;

          return (
            <div
              key={`keyword-${index}`}
              className={twMerge(
                "cursor-pointer p-3",
                index !== 0 ? "border-t border-gray-900/5" : "",
                selectedIndex === index
                  ? "bg-gray-50 text-indigo-600"
                  : "text-gray-700 hover:bg-gray-50 hover:text-indigo-600",
              )}
              onClick={() => setSelectedIndex(index)}
            >
              <div className="text-sm font-semibold">{entry.phrase}</div>
              <div
                className={twMerge(
                  "mt-2 flex text-sm",
                  selectedIndex === index
                    ? "text-indigo-600"
                    : "text-muted-foreground",
                )}
              >
                <InsightBar
                  color={Color.Blue}
                  percentageWidth={interviewPercentage}
                  label={`${interviewPercentage}% of interviews`}
                />
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex max-h-[1000px] flex-col space-y-4 overflow-y-auto border-l border-border-light p-4">
        {keywordTracker.type === "competitive"
          ? selectedKeyword.analyses.map((a, index) => (
              <KeywordAnalysisCard
                key={`keyword-analysis-${index}`}
                keywordAnalysis={a}
                competitorName={selectedKeyword.phrase}
              />
            ))
          : null}
        {renderQuotesSection()}
      </div>
    </div>
  );
};
